import React, { useCallback, useMemo, useRef, useState } from 'react';
import Map, { Layer, Source } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Box, Button, makeStyles } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import { useTranslate } from 'react-admin';

import { SOURCE_BOARDS } from '../../providers/resources';
import { MapAvatarsv2Props, PopupInfo, ViewportState } from './types';
import { DEFAULT_VIEWPORT, MAP_CONFIG } from './constants';
import { useMapData } from './hooks/useMapData';
import { useAvatarDetail } from './hooks/useAvatarDetail';
import { useMapFilters } from './hooks/useMapFilters';
import { FilterPanel } from './components/FilterPanel';
import { LoadingOverlay } from './components/LoadingOverlay';
import { MapDebugPanel } from './components/MapDebugPanel';
import { AvatarPopup } from './components/AvatarPopup/AvatarPopup';
import './MapAvatarsV2.css';

const useStyles = makeStyles({
    container: {
        position: 'relative',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
    },
    filterButton: {
        position: 'absolute',
        top: '16px',
        right: '16px',
        zIndex: 1000,
        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
        textTransform: 'none',
        fontSize: '14px',
        padding: '8px 16px',
    },
    mapContainer: {
        position: 'relative',
        flex: 1,
        height: '100%',
        overflow: 'hidden',
    },
});

const MapAvatarsV2: React.FC<MapAvatarsv2Props> = ({ mapHeight = '100vh', initialLocation, externalFilters, resource = SOURCE_BOARDS }) => {
    const classes = useStyles();
    const t = useTranslate();
    const mapRef = useRef<any>(null);

    // Map configuration
    const mapboxAccessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';
    const apiBase = process.env.REACT_APP_API_SS_ENTRYPOINT || '';
    const urlBase = useMemo(() => {
        const base = apiBase.endsWith('/') ? apiBase.slice(0, -1) : apiBase;
        return `${base}/${resource}`;
    }, [apiBase, resource]);

    // State management
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [popupInfo, setPopupInfo] = useState<PopupInfo | null>(null);
    const [viewport, setViewport] = useState<ViewportState>(() => ({
        latitude: initialLocation?.latitude ?? DEFAULT_VIEWPORT.latitude,
        longitude: initialLocation?.longitude ?? DEFAULT_VIEWPORT.longitude,
        zoom: DEFAULT_VIEWPORT.zoom,
    }));

    // Custom hooks
    const { filters, updateFilter, clearFilters } = useMapFilters();
    const { data, loading, paginationInfo } = useMapData(filters, urlBase);
    const {
        detail,
        loading: loadingDetail,
        error: errorDetail,
    } = useAvatarDetail(popupInfo?.avatarId || null, urlBase);

    // Map layers configuration
    const clusterLayer: any = useMemo(
        () => ({
            id: 'clusters',
            type: 'circle',
            source: 'avatars',
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': [
                    'case',
                    ['>=', ['coalesce', ['get', 'maxThresholdLevel'], 0], 3],
                    '#ef2929', // Red for threshold.level.3
                    ['>=', ['coalesce', ['get', 'maxThresholdLevel'], 0], 2],
                    '#ff9800', // Orange for threshold.level.2
                    ['>=', ['coalesce', ['get', 'maxThresholdLevel'], 0], 1],
                    '#4caf50', // Green for threshold.level.1
                    '#9e9e9e', // Gray for null/default
                ],
                'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
            },
        }),
        [],
    );

    const clusterCountLayer: any = useMemo(
        () => ({
            id: 'cluster-count',
            type: 'symbol',
            source: 'avatars',
            filter: ['has', 'point_count'],
            layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12,
            },
        }),
        [],
    );

    const unclusteredPointLayer: any = useMemo(
        () => ({
            id: 'unclustered-point',
            type: 'circle',
            source: 'avatars',
            filter: ['!', ['has', 'point_count']],
            paint: {
                'circle-color': ['get', 'thresholdColor'],
                'circle-radius': 8,
                'circle-stroke-width': 1,
                'circle-stroke-color': '#fff',
            },
        }),
        [],
    );

    // Event handlers
    const onClosePopup = useCallback(() => {
        setPopupInfo(null);
    }, []);

    const onMapClick = useCallback(
        (event: any) => {
            const feature = event?.features?.[0];

            if (!feature) {
                onClosePopup();
                return;
            }

            const { properties, geometry } = feature;
            const avatarId = properties?.avatarId;
            const clusterId = properties?.cluster_id || properties?.clusterId;

            const coordinates = geometry?.coordinates;
            if (!coordinates || !Array.isArray(coordinates) || coordinates.length < 2) return;

            const [longitude, latitude] = coordinates;
            if (isNaN(longitude) || isNaN(latitude) || !isFinite(longitude) || !isFinite(latitude)) return;

            if (clusterId && mapRef.current) {
                const map = mapRef.current.getMap();
                const source: any = map.getSource('avatars');
                if (source && typeof source.getClusterExpansionZoom === 'function') {
                    source.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
                        if (err) return;
                        setViewport((prev: ViewportState) => ({ ...prev, longitude, latitude, zoom }));
                    });
                }
            } else if (avatarId) {
                setPopupInfo({ avatarId, longitude, latitude, feature });
            }
        },
        [onClosePopup],
    );


    const geojson = useMemo(() => data, [data]);

    console.log('geojson', geojson);


    return (
        <Box className={classes.container}>
            {/* Filter Button */}
            <Button
                variant='contained'
                color='primary'
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters(!showFilters)}
                className={classes.filterButton}
            >
                {t('app.ancs.actions.personalize')}
            </Button>

            {/* Filter Panel */}
            {showFilters && (
                <FilterPanel
                    filters={filters}
                    onFilterChange={updateFilter}
                    onClear={clearFilters}
                    onClose={() => setShowFilters(false)}
                />
            )}

            {/* Map Container */}
            <Box className={classes.mapContainer}>
                {loading && <LoadingOverlay paginationInfo={paginationInfo} />}

                <Map
                    ref={mapRef}
                    initialViewState={viewport as any}
                    mapStyle={process.env.REACT_APP_MAP_STYLE || 'mapbox://styles/mapbox/streets-v11'}
                    mapboxAccessToken={mapboxAccessToken}
                    interactiveLayerIds={[unclusteredPointLayer.id, clusterLayer.id]}
                    onClick={onMapClick}
                    style={{ width: '100%', height: '100%' }}
                >
                    <Source
                        id='avatars'
                        type='geojson'
                        data={geojson as any}
                        cluster={true}
                        clusterMaxZoom={MAP_CONFIG.CLUSTER_MAX_ZOOM}
                        clusterRadius={MAP_CONFIG.CLUSTER_RADIUS}
                        clusterProperties={{
                            maxThresholdLevel: ['max', ['get', 'thresholdLevel']],
                        }}
                    >
                        <Layer {...clusterLayer} />
                        <Layer {...clusterCountLayer} />
                        <Layer {...unclusteredPointLayer} />
                    </Source>

                    {popupInfo && (
                        <AvatarPopup
                            detail={detail}
                            loading={loadingDetail}
                            error={errorDetail}
                            longitude={popupInfo.longitude}
                            latitude={popupInfo.latitude}
                            onClose={onClosePopup}
                            feature={popupInfo.feature}
                        />
                    )}
                </Map>

                {/* Debug Panel */}
                <MapDebugPanel data={geojson} paginationInfo={paginationInfo} />
            </Box>
        </Box>
    );
};

export default MapAvatarsV2;
